import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, Navigation } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { RoleplayApiService } from '../../service/roleplay.service';
import { OS, RoleplayFeedbackDetail, Score, Skill } from '../../model/rolePlay';
import { SubSkillDescriptionDialogComponent } from './sub-skill-description-dialog/sub-skill-description-dialog.component';
import { LikeDislikeDialogComponent } from '../../v2/shared/components/ui/dialogs';
import { RoleplayFeedbackUserEvalCreate, RoleplayFeedbackUserEvalMatch, RoleplayFeedbackUserEvalMatchResponse, RoleplayFeedbackUserEvalResponse, RoleplayFeedbackUserEvalUpdate } from '../../model/roleplay/roleplay-feedback-user-eval.model';
import { PlatformRegionService } from '../../service/shared/platform-region.service';
import { RoleplayFeedbackUserEvalApiService } from '../../v2/apis/roleplay/roleplay-feedback-user-eval-api.service';
import { ToastrService } from 'ngx-toastr';

type DialogType = 'like' | 'dislike';
@Component({
    selector: 'app-roleplay-feedback',
    templateUrl: './roleplay-feedback.component.html',
    styleUrl: './roleplay-feedback.component.scss',
    standalone: false
})
export class RoleplayFeedbackComponent implements OnInit {
  SKILLS_TO_DISPLAY = 2;
  SCORES_TO_DISPLAY = this.SKILLS_TO_DISPLAY + 2;
  chatSessionid!: number;
  scenario_name: string;
  scenario_summary: string;
  scenario_objective: string;
  user_objective: string;
  created_date: string;
  os: OS | null = null;
  scores: Score[] | null = null;
  skills: Skill[] | null = null;

  icons: any = {
    like: '../../../assets/icons/icon-positive-feedback-grey.svg',
    dislike: '../../../assets/icons/icon-negative-feedback-grey.svg',
  }

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private readonly rolePlayService: RoleplayApiService,
    private router: Router,
    private dialog: MatDialog,
    private platformRegionService: PlatformRegionService,
    private roleplayFeedbackUserEvalApiService: RoleplayFeedbackUserEvalApiService,
    private toastr: ToastrService
  ) {
    const nav = this.router.getCurrentNavigation();
  }

  ngOnInit(): void {
    this.chatSessionid = Number(this.route.snapshot.paramMap.get('id'));
    this.fetchFeedbackData();
  }

  fetchFeedbackData() {
    this.rolePlayService.getRoleplayFeedbackSessionId(this.chatSessionid).subscribe((feedback_data: RoleplayFeedbackDetail) => {
      this.scenario_name = feedback_data.info.scenario_name;
      this.scenario_summary = feedback_data.info.scenario_summary;
      this.scenario_objective = feedback_data.info.scenario_objective;
      this.user_objective = feedback_data.info.user_objective;
      this.created_date = feedback_data.info.created_date;
      this.os = feedback_data.detail?.OS;
      this.scores = feedback_data.scores;
      this.skills = feedback_data.detail?.Skills;
    });
  }

  getScoreColorClass(i: number): string {
    switch (i) {
      case 0:
        return 'one';
      case 1:
        return 'two';
      case 2:
        return 'three';
      case 3:
        return 'four';
      case 4:
        return 'five';
      default:
        return '';
    }
  }

  getFormattedScore(item: any): string {
    // Check if score is a non-numeric string (error message)
    if (typeof item.score === 'string' && isNaN(parseFloat(item.score))) {
      return item.score;
    }
    
    // Convert to numbers if they're strings
    const score = typeof item.score === 'string' ? parseFloat(item.score) : item.score;
    const max_score = typeof item.max_score === 'string' ? parseFloat(item.max_score) : item.max_score;
    
    if (item.key && item.key.toLowerCase().includes('(%)')) {
      return score + '%';
    }
    
    return `${score}/${max_score}`;
  }

  isNumericScore(score: any, max_score: any): boolean {
    // Check if score is a number or a string representation of a number
    const scoreIsNumeric = typeof score === 'number' || (typeof score === 'string' && !isNaN(parseFloat(score)));
    // Check if max_score is a number or a string representation of a number
    const maxScoreIsNumeric = typeof max_score === 'number' || (typeof max_score === 'string' && !isNaN(parseFloat(max_score)));
    
    return scoreIsNumeric && maxScoreIsNumeric;
  }

  showOutOf(key: string): boolean {
    // only hide "out of" text for percentage
    return !key.toLowerCase().includes('(%)');
  }

  openSubSkillDescription(topic: string, description: string): void {
    this.dialog.open(SubSkillDescriptionDialogComponent, {
      data: { topic, description }
    });
  }

  giveFeedback(dialogType: DialogType, skill_name: string, sub_skill_name: string): void {
    const payload: RoleplayFeedbackUserEvalMatch = {
      chat_session_id: this.chatSessionid,
      skill_name: skill_name,
      sub_skill_name: sub_skill_name,
      feedback_type: dialogType === 'like' ? 1 : 0,
      business_unit_code: this.platformRegionService.selected_platform_code,
      region_code: this.platformRegionService.selected_region_code
    };
    this.roleplayFeedbackUserEvalApiService.match(payload).subscribe({
      next: (response: RoleplayFeedbackUserEvalMatchResponse) => {
        console.log('Feedback matched successfully:', response);
        if (response.data && response.data.id) {
          this.openExistingFeedbackDialog(dialogType, response.data);
        } else {
          this.openNewFeedbackDialog(dialogType, skill_name, sub_skill_name);
        }
      },
      error: (error) => {
        console.error('Error matching feedback:', error);
      }
    });
  }

  openExistingFeedbackDialog(dialogType: DialogType, response: RoleplayFeedbackUserEvalResponse): void {
    const dialog_data = {
      dialogType: dialogType,
      existingData: {
        selectedOption: response.satisfactory_level,
        comment: response.comment
      }
    }

    const dialogRef = this.dialog.open(LikeDislikeDialogComponent, {
      data: dialog_data
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('Positive feedback submitted:', result);
      if (result) {
        this.updateFeedback(result.selectedOption, result.comment, response.id);
      }
    });
  }

  openNewFeedbackDialog(dialogType: DialogType, skill_name: string, sub_skill_name: string): void {
    const dialog_data = {
      dialogType: dialogType,
    }

    const dialogRef = this.dialog.open(LikeDislikeDialogComponent, {
      data: dialog_data
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('Positive feedback submitted:', result);
      if (result) {
        this.saveUserFeedback(dialogType, result.selectedOption, result.comment, skill_name, sub_skill_name);
      }
    });
  }

  updateFeedback(satisfactory_level: number, comment: string, responseId: number): void {
    const feedbackData: RoleplayFeedbackUserEvalUpdate = {
      id: responseId,
      satisfactory_level: satisfactory_level,
      comment: comment,
      updated_by: Number(sessionStorage.getItem('userId'))
    };

    this.roleplayFeedbackUserEvalApiService.update(feedbackData).subscribe(response => {
      this.toastr.success('Feedback updated successfully!', 'Success');
      console.log('Feedback updated successfully:', response);
    });
  }

  saveUserFeedback(dialogType: 'like' | 'dislike', satisfactory_level: number, comment: string, skill_name: string, sub_skill_name: string): void {
    const feedbackData: RoleplayFeedbackUserEvalCreate = {
      chat_session_id: this.chatSessionid,
      skill_name: skill_name,
      sub_skill_name: sub_skill_name,
      satisfactory_level: satisfactory_level,
      comment: comment,
      feedback_type: dialogType === 'like' ? 1 : 0,
      business_unit_code: this.platformRegionService.selected_platform_code,
      region_code: this.platformRegionService.selected_region_code,
      create_by: Number(sessionStorage.getItem('userId'))
    };

    this.roleplayFeedbackUserEvalApiService.create(feedbackData).subscribe(response => {
      this.toastr.success('Feedback added successfully!', 'Success');
      console.log('Feedback saved successfully:', response);
    });
  }

}
